import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Info, ExternalLink } from 'lucide-react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import usePermissions from '../../auth/usePermissions';
import RetainerModal from '../../components/Projects/Retainer/RetainerModal';
import RetainerFolioModal from '../../components/Projects/Retainer/RetainerFolioModal';
import folderIcon from '../../assets/folder-icon.png';
import RefundRetainerModal from '../../components/Projects/Retainer/RefundRetainerModal';
import '../../component-styles/RecordPages.css';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

function mapRetainer(r) {
  const total = Number(r.amount || 0);
  const funded = Number(r.payments || 0);
  const applied = Number(r.appliedAmount || 0);
  const outstanding = Math.max(total - funded, 0);

  return {
    ...r,
    total,
    funded,
    applied,
    outstanding,
    availableBalance: Math.max(funded - applied, 0),
    date: r.requestDate,
  };
}

export default function RetainersSection() {
  const navigate = useNavigate();
  const { projectId } = useOutletContext();

  const { permissions } = usePermissions();
  const canCreate = permissions.createApplyCreditsRetainers !== false;

  const [retainers, setRetainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingQuickBooks, setSyncingQuickBooks] = useState(false);
  const [qbAutoCreateEnabled, setQbAutoCreateEnabled] = useState(false);
  const [popover, setPopover] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [showFolio, setShowFolio] = useState(null);

  async function loadRetainers() {
    if (!projectId) return;

    try {
      const userId = localStorage.getItem('userId');

      const res = await fetch(`${API_BASE}/api/retainers/${projectId}`, {
        headers: { userId },
      });

      const json = await res.json();
      const arr = Array.isArray(json) ? json : json.Items || json.item || [];

      setRetainers(
        arr.map(mapRetainer).sort((a, b) => {
          const numA = Number((a.retainerNumber || '').replace(/\D/g, ''));
          const numB = Number((b.retainerNumber || '').replace(/\D/g, ''));
          return numA - numB;
        })
      );
    } catch (err) {
      console.error('Error loading retainers:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!projectId) return;

    loadRetainers();
  }, [projectId, API_BASE]);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('idToken');
    if (!userId || !token) return;

    (async () => {
      try {
        const [settingsRes, qbStatusRes] = await Promise.all([
          fetch(`${API_BASE}/api/settings/${userId}`),
          fetch(`${API_BASE}/api/quickbooks/status`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (!settingsRes.ok || !qbStatusRes.ok) return;
        const settings = await settingsRes.json();
        const qbStatus = await qbStatusRes.json().catch(() => ({}));
        setQbAutoCreateEnabled(
          Boolean(settings?.quickbooks?.autoCreateOnSave) && Boolean(qbStatus?.connected)
        );
      } catch {
        // ignore
      }
    })();
  }, [API_BASE]);

  const totals = useMemo(() => {
    return {
      funded: retainers.reduce((s, r) => s + (r.funded || 0), 0),
      applied: retainers.reduce((s, r) => s + (r.applied || 0), 0),
      available: retainers.reduce((s, r) => s + (r.availableBalance || 0), 0),
    };
  }, [retainers]);

  const handleSaveRetainer = async (form) => {
    try {
      const userId = localStorage.getItem('userId');

      const payload = {
        userId,
        projectId,
        requestDate: form.requestDate,
        dueDate: form.dueDate,
        description: form.description,
        amount: Number(form.amount),
      };

      const res = await fetch(`${API_BASE}/api/retainers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        notify("Your changes couldn't be saved. Please try again.");
        return;
      }

      setRetainers((prev) =>
        [...prev, mapRetainer(data.item)].sort((a, b) => {
          const numA = Number((a.retainerNumber || '').replace(/\D/g, ''));
          const numB = Number((b.retainerNumber || '').replace(/\D/g, ''));
          return numA - numB;
        })
      );

      setShowModal(false);

      if (qbAutoCreateEnabled) {
        fetch(`${API_BASE}/api/retainers/${projectId}/sync-quickbooks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', userId },
          body: JSON.stringify({ userId }),
        }).catch(() => {});
      }
    } catch (err) {
      console.error('Save error:', err);
      notify('Error saving retainer');
    }
  };

  const handleSyncQuickBooks = async () => {
    try {
      const userId = localStorage.getItem('userId');
      setSyncingQuickBooks(true);

      const res = await fetch(`${API_BASE}/api/retainers/${projectId}/sync-quickbooks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          userId,
        },
        body: JSON.stringify({ userId }),
      });

      const data = await res.json();
      if (!res.ok) {
        notify(data.error || 'Failed to sync retainers to QuickBooks.');
        return;
      }

      await loadRetainers();

      notify(
        `QuickBooks retainer sync complete. Synced ${data.syncedCount || 0}, failed ${data.failedCount || 0}, skipped ${data.skippedCount || 0}.`
      );
    } catch (err) {
      console.error('QuickBooks retainer sync failed:', err);
      notify('Failed to sync retainers to QuickBooks.');
    } finally {
      setSyncingQuickBooks(false);
    }
  };

  return (
    <section className="record-section">
      <div className="record-top-row">
        <h2 className="record-title">Retainers</h2>

        <div className="record-controls">
          <button
            className="record-add-btn"
            onClick={handleSyncQuickBooks}
            disabled={syncingQuickBooks || loading || retainers.length === 0}
          >
            <span>{syncingQuickBooks ? 'Syncing…' : 'Sync QuickBooks'}</span>
          </button>
          {canCreate && (
            <button className="record-add-btn" onClick={() => setShowModal(true)}>
              <Plus size={14} /> <span>Add New</span>
            </button>
          )}
        </div>
      </div>

      <div className="record-card">
        <div className="record-metrics">
          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.available)}</div>
            <div className="record-metric-label">Available</div>
          </div>

          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.funded)}</div>
            <div className="record-metric-label">Funded</div>
          </div>

          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.applied)}</div>
            <div className="record-metric-label">Applied</div>
          </div>
        </div>

        {/* Table */}
        <div className="record-table-wrap">
          {loading ? (
            <div className="record-empty">Loading retainers…</div>
          ) : retainers.length === 0 ? (
            <div className="record-empty">No retainers yet.</div>
          ) : (
            <table className="record-table retainers-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Retainer #</th>
                  <th>Folio</th>
                  <th>Description</th>
                  <th>Total Due</th>
                  <th>Funded</th>
                  <th>Applied</th>
                  <th>Available</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {retainers.map((r) => (
                  <tr
                    key={r.retainerId}
                    className="clickable-row"
                    onClick={() => navigate(`/projects/${projectId}/retainers/${r.retainerId}`)}
                  >
                    <td>{r.date || '—'}</td>

                    <td>
                      <button
                        className="link-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/projects/${projectId}/retainers/${r.retainerId}`);
                        }}
                      >
                        {r.retainerNumber}
                      </button>
                    </td>

                    <td className="folio-cell">
                      <div
                        className="folio-item folio-click"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowFolio(r);
                        }}
                      >
                        <img
                          src="/folder-icon.png"
                          alt="Open folio"
                          className="link-icon"
                          width={18}
                          height={18}
                          draggable={false}
                        />
                      </div>
                    </td>

                    <td className="desc-cell">
                      {r.description}
                      {r.appliedInvoices?.length > 0 && (
                        <button
                          className="info-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPopover(popover === r.retainerId ? null : r.retainerId);
                          }}
                        >
                          <Info size={14} />
                        </button>
                      )}
                    </td>

                    <td>{formatCurrency(r.outstanding)}</td>
                    <td>{formatCurrency(r.funded)}</td>
                    <td>{formatCurrency(r.applied)}</td>
                    <td>{formatCurrency(r.availableBalance)}</td>

                    <td>
                      {r.availableBalance > 0 && (
                        <button
                          className="link-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRefundTarget(r);
                          }}
                        >
                          Refund
                        </button>
                      )}
                    </td>

                    {popover === r.retainerId && (
                      <div className="retainer-popover">
                        <h4>
                          {r.retainerNumber} <span>{r.description}</span>
                        </h4>
                        <ul>
                          {r.appliedInvoices?.map((a, i) => (
                            <li key={i}>
                              <ExternalLink size={13} /> <b>{a.invoiceNumber}</b> –{' '}
                              {formatCurrency(a.amount)} <span>{a.date}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <RetainerModal
          nextNumber="Assigned on save"
          onClose={() => setShowModal(false)}
          onSave={handleSaveRetainer}
        />
      )}

      {refundTarget && (
        <RefundRetainerModal
          retainer={refundTarget}
          projectId={projectId}
          onClose={() => setRefundTarget(null)}
          onRefunded={(data) => {
            setRetainers((prev) =>
              prev.map((r) =>
                r.retainerId === data.item.retainerId
                  ? mapRetainer({
                      ...r,
                      payments: data.funded,
                      availableBalance: data.available,
                    })
                  : r
              )
            );
          }}
        />
      )}

      {showFolio && (
        <RetainerFolioModal
          retainer={showFolio}
          projectId={projectId}
          onClose={() => setShowFolio(null)}
        />
      )}
    </section>
  );
}
